// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { useContext } from 'react';
import Lottie from 'lottie-react';
import { ThemeContext } from '../context/ThemeContext';
import forkliftDarkAnimation from '../assets/animations/loader_animation_dark.json';
import forkliftLightAnimation from '../assets/animations/loader_animation_light.json';
import { cn } from '../utils/cn';

const getUiMode = (themeContext) => {
  if (themeContext?.uiMode) return themeContext.uiMode;

  try {
    const cached = localStorage.getItem('stamp_app_mode');
    if (cached === 'light-mode' || cached === 'dark-mode') return cached;
  } catch {
    // ignore
  }

  return 'dark-mode';
};

const LoadingScreen = ({ loading = 'Loading...', fullscreen = true, message = '' }) => {
  const themeContext = useContext(ThemeContext);
  const isDarkTheme = getUiMode(themeContext) !== 'light-mode';
  const textClassName = cn('text-2xl font-medium', isDarkTheme ? 'text-[#90a1b9]' : 'text-[#454756]');

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        fullscreen ? 'h-screen w-screen' : 'h-full w-full min-h-0 flex-1 self-stretch',
        isDarkTheme ? fullscreen && 'bg-dark-background' : 'bg-white',
      )}
    >
      <Lottie
        animationData={isDarkTheme ? forkliftDarkAnimation : forkliftLightAnimation}
        loop
        className='h-[130px] w-[200px] pointer-events-none overflow-hidden [&_svg]:block [&_svg]:!h-full [&_svg]:!w-full'
        rendererSettings={{ preserveAspectRatio: 'xMidYMid slice' }}
      />
      <p className={textClassName}>{loading}</p>
      {message && <p className={textClassName}>{message}</p>}
    </div>
  );
};

export default LoadingScreen;
